import React from 'react';
import { Box, Switch, SwitchProps } from '@mui/material';
import { styled } from '@mui/material/styles';

type StatusIconInput = string | React.ReactNode;

export interface MaStatusSwitchOption {
  value: string;
  icon?: StatusIconInput;
  text?: string;
  thumbColor: string;
  iconColor: string;
  tooltip: string;
}

interface MaStatusSwitchProps extends Omit<SwitchProps, 'icon' | 'checkedIcon'> {
  offIcon?: StatusIconInput;
  onIcon?: StatusIconInput;
  offThumbColor?: string;
  onThumbColor?: string;
  offIconColor?: string;
  onIconColor?: string;
  offTrackColor?: string;
  onTrackColor?: string;
  options?: MaStatusSwitchOption[];
  selectedValue?: string;
  onSelectedValueChange?: (value: string) => void;
}

interface StyledMaStatusSwitchProps {
  offTrackColor: string;
  onTrackColor: string;
}

const StyledMaStatusSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== 'offTrackColor' && prop !== 'onTrackColor',
})<StyledMaStatusSwitchProps>(({ offTrackColor, onTrackColor }) => ({
  width: 62,
  height: 34,
  padding: 7,
  overflow: 'visible',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      transform: 'translateX(22px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: onTrackColor,
      },
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 1,
    },
  },
  '& .MuiSwitch-thumb': {
      width: 32,
      height: 32,
    borderRadius: '50%',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
      overflow: 'hidden',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    height: 16,
    borderRadius: 8,
    position: 'relative',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: offTrackColor,
  },
}));

const renderStatusIcon = (icon: StatusIconInput, color: string, size = 24): React.ReactNode => {
  if (typeof icon === 'string') {
    return React.createElement(
      'span',
      {
        className: 'material-icons material-symbols-rounded',
        style: {
          color,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${size}px`,
          lineHeight: 1,
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
        },
      },
      icon
    );
  }

  return React.createElement(
    'span',
    {
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        lineHeight: 1,
      },
    },
    icon
  );
};

const StatusKnob: React.FC<{
  icon?: StatusIconInput;
  text?: string;
  iconSize?: number;
  thumbColor: string;
  iconColor: string;
}> = ({ icon, text, iconSize, thumbColor, iconColor }) => {
  return React.createElement(
    Box,
    {
      sx: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: thumbColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'none',
        flexShrink: 0,
        ...(text && {
          '&::after': {
            content: JSON.stringify(text),
            color: iconColor,
            fontSize: '11px',
            fontWeight: 600,
            lineHeight: 1,
          },
        }),
      },
    },
    text
      ? null
      : renderStatusIcon(icon, iconColor, iconSize)
  );
};

const MultiStateStatusSwitch: React.FC<{
  options: MaStatusSwitchOption[];
  selectedValue?: string;
  disabled?: boolean;
  onSelectedValueChange?: (value: string) => void;
}> = ({ options, selectedValue, disabled, onSelectedValueChange }) => {
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === selectedValue));
  const stepWidth = 26;
  const railWidth = 32 + Math.max(0, options.length - 1) * stepWidth;

  return React.createElement(
    Box,
    {
      role: 'radiogroup',
      sx: {
        position: 'relative',
        display: 'inline-block',
        alignItems: 'center',
        height: 20,
        width: railWidth,
        flexShrink: 0,
        px: '2px',
        borderRadius: 10,
        backgroundColor: '#aab4be',
        overflow: 'visible',
      },
    },
    options.map((option, index) => React.createElement(
      'button',
      {
        key: option.value,
        type: 'button',
        role: 'radio',
        'aria-checked': selectedValue === option.value,
        'aria-label': option.tooltip,
        title: option.tooltip,
        disabled,
        onClick: () => onSelectedValueChange?.(option.value),
        style: {
          width: 32,
          height: 32,
          padding: 0,
          border: 0,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          position: 'absolute',
          left: `${index * stepWidth}px`,
          top: '-6px',
          zIndex: 1,
        },
      },
      React.createElement(
        'span',
        {
          className: 'material-icons material-symbols-rounded',
          style: {
            color: 'rgba(0, 0, 0, 0.25)',
            display: option.text ? 'none' : 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            lineHeight: 1,
            width: '14px',
            height: '14px',
          },
        }
        , typeof option.icon === 'string' ? option.icon : undefined
      )
    )),
    React.createElement(
      Box,
      {
        sx: {
          position: 'absolute',
          left: 0,
          top: '-6px',
          zIndex: 2,
          pointerEvents: 'none',
          transform: `translateX(${selectedIndex * stepWidth}px)`,
          transition: 'transform 180ms ease-out',
        },
      },
      React.createElement(StatusKnob, {
        icon: options[selectedIndex]?.icon,
        text: options[selectedIndex]?.text,
        iconSize: 24,
        thumbColor: options[selectedIndex]?.thumbColor ?? '#ffffff',
        iconColor: options[selectedIndex]?.iconColor ?? '#9e9e9e',
      })
    )
  );
};

const MaStatusSwitch: React.FC<MaStatusSwitchProps> = ({
  offIcon = 'toggle_off',
  onIcon = 'toggle_on',
  offThumbColor = '#fafafa',
  onThumbColor = '#4caf50',
  offIconColor = '#9e9e9e',
  onIconColor = '#2e7d32',
  offTrackColor = '#aab4be',
  onTrackColor = '#aab4be',
  options,
  selectedValue,
  onSelectedValueChange,
  ...switchProps
}) => {
  if (options && options.length > 0) {
    return React.createElement(MultiStateStatusSwitch, {
      options,
      selectedValue,
      disabled: switchProps.disabled,
      onSelectedValueChange,
    });
  }

  return React.createElement(StyledMaStatusSwitch, {
    offTrackColor,
    onTrackColor,
    icon: React.createElement(StatusKnob, { icon: offIcon, thumbColor: offThumbColor, iconColor: offIconColor }),
    checkedIcon: React.createElement(StatusKnob, { icon: onIcon, thumbColor: onThumbColor, iconColor: onIconColor }),
    ...switchProps,
  });
};

export default MaStatusSwitch;
